/**
 * Wearable Jewelry theme — page transition overlay.
 *
 * Plays a short full-screen video on selected internal link clicks, then
 * navigates to the link target when the video ends. Includes throttling,
 * accessibility fallbacks, and skip rules tuned to this theme's drawers,
 * filters, and quick view.
 */
class TransitionContainer extends HTMLElement {
  constructor() {
    super();
    this.cookieClicks = "wt_transition_clicks";
    this.cookieShown = "wt_transition_shown";
    this.count = parseInt(this.getCookie(this.cookieClicks), 10) || 0;
    this.shown = parseInt(this.getCookie(this.cookieShown), 10) || 0;
    this.targetUrl = null;
  }

  connectedCallback() {
    this.video = this.querySelector("video");
    this.frequency = this.getAttribute("data-frequency") || "growth_curve";
    this.skipReducedMotion = this.getAttribute("data-skip-reduced-motion") === "true";

    var raw = this.getAttribute("data-video-urls") || "";
    this.urls = raw
      .split(",")
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);

    if (!this.video || this.urls.length === 0) return;

    var source = document.createElement("source");
    source.src = this.urls[Math.floor(Math.random() * this.urls.length)];
    source.type = "video/mp4";
    this.video.appendChild(source);

    this.onClick = this.onClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);

    document.addEventListener("click", this.onClick);
    window.addEventListener("pageshow", this.onPageShow);
    this.video.addEventListener("ended", this.onEnded);
    this.video.addEventListener("error", this.onError);
  }

  prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  onClick(e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest("a");
    if (!link) return;

    var href = link.getAttribute("href");
    if (!href) return;

    if (link.target && link.target !== "_self") return;
    if (link.hasAttribute("download")) return;
    if (/^(mailto:|tel:|#)/i.test(href)) return;

    // Same-origin only.
    if (!link.href.startsWith(window.location.origin)) return;

    // In-page anchors / hash navigations.
    if (href.indexOf("#") !== -1) {
      var url = new URL(link.href);
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return;
      }
    }

    if (this.shouldSkip(e.target)) return;

    if (this.skipReducedMotion && this.prefersReducedMotion()) return;

    this.incrementClicks();
    if (!this.shouldPlay()) return;

    e.preventDefault();
    this.targetUrl = link.href;
    this.play();
  }

  /** True when the click originates from UI that should not trigger a transition. */
  shouldSkip(target) {
    var skipSelectors = [
      "[data-transition-disabled]",
      "[data-cart-open]",
      "[data-search-open]",
      "[data-favourites-open]",
      "[data-account-open]",
      "[data-contact-open]",
      "[data-enquiry-open]",
      "[data-quick-view]",
      "[data-quick-add]",
      "[data-collection-filters]",
      "[data-collection-filter-open]",
      "[data-collection-filter-close]",
      "[data-quick-filter-link]",
      ".collection-quick-filters",
    ];
    return Boolean(target.closest(skipSelectors.join(",")));
  }

  /** Throttle policy: returns true when this click should show the overlay. */
  shouldPlay() {
    switch (this.frequency) {
      case "every_click":
        this.incrementShown();
        return true;
      case "every_3rd":
        return this.everyNth(3);
      case "every_5th":
        return this.everyNth(5);
      case "growth_curve":
      default:
        return this.growthCurve();
    }
  }

  everyNth(n) {
    if (this.count % n === 0) {
      this.incrementShown();
      return true;
    }
    return false;
  }

  /** Growth curve: plays on clicks 1, 4, 10, 19, ... */
  growthCurve() {
    var n = this.shown + 1;
    var target = (3 / 2) * n * n - (3 / 2) * n + 1;
    if (this.count >= target) {
      this.incrementShown();
      return true;
    }
    return false;
  }

  play() {
    this.classList.remove("is-hidden");
    document.body.style.overflow = "hidden";
    var playback = this.video.play();
    if (playback && typeof playback.catch === "function") {
      var self = this;
      playback.catch(function () {
        self.navigate();
      });
    }
  }

  onEnded() {
    this.navigate();
  }

  onError() {
    this.navigate();
  }

  navigate() {
    if (this.targetUrl) {
      window.location.href = this.targetUrl;
    }
  }

  onPageShow(event) {
    if (event.persisted) {
      this.classList.add("is-hidden");
      document.body.style.overflow = "";
      if (this.video) {
        this.video.pause();
        this.video.currentTime = 0;
      }
    }
  }

  incrementClicks() {
    this.count += 1;
    this.setCookie(this.cookieClicks, this.count);
  }

  incrementShown() {
    this.shown += 1;
    this.setCookie(this.cookieShown, this.shown);
  }

  getCookie(name) {
    var matches = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, "\\$1") + "=([^;]*)"),
    );
    return matches ? decodeURIComponent(matches[1]) : undefined;
  }

  setCookie(name, value) {
    document.cookie =
      encodeURIComponent(name) + "=" + encodeURIComponent(value) + "; path=/; max-age=31536000; SameSite=Lax";
  }
}

if (!customElements.get("transition-container")) {
  customElements.define("transition-container", TransitionContainer);
}
